/**
 * SoleadLab 2.0 — Evidence List Component
 *
 * B3.1: Minimal consumption path for evidence entities from backend authority.
 *
 * Displays evidence items for the current site in a compact card list.
 * Read-only — evidence is immutable after creation.
 *
 * Usage:
 *   <EvidenceList siteId="site-123" />
 *
 * The component sets the site scope on the evidence store,
 * fetches items on mount, and renders each item as a card
 * showing type, original filename, capture date, and file size.
 */

import { useEffect } from 'react';
import { useEvidenceStore } from '@/stores/evidenceStore';

interface EvidenceListProps {
  siteId: string;
  typeFilter?: string;
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function EvidenceList({ siteId, typeFilter }: EvidenceListProps) {
  const items = useEvidenceStore((s) => s.items);
  const loading = useEvidenceStore((s) => s.loading);
  const error = useEvidenceStore((s) => s.error);
  const setSiteId = useEvidenceStore((s) => s.setSiteId);
  const fetchItems = useEvidenceStore((s) => s.fetchItems);

  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (siteId) {
      fetchItems(typeFilter);
    }
  }, [siteId, typeFilter, fetchItems]);

  if (loading) {
    return <div className="evidence-list__status">Loading evidence...</div>;
  }

  if (error) {
    return <div className="evidence-list__status evidence-list__error">{error}</div>;
  }

  if (items.length === 0) {
    return <div className="evidence-list__status">No evidence items found.</div>;
  }

  return (
    <div className="evidence-list">
      <div className="evidence-list__header">
        {items.length} evidence item{items.length !== 1 ? 's' : ''}
      </div>
      <div className="evidence-list__items">
        {items.map((item) => (
          <div key={item.id} className="evidence-list__card">
            <div className="evidence-list__card-type">
              {item.evidenceType}
            </div>
            <div className="evidence-list__card-details">
              {item.originalName && (
                <span className="evidence-list__card-name" title={item.originalName}>
                  {item.originalName}
                </span>
              )}
              {item.captureDate && (
                <span className="evidence-list__card-date">
                  {formatDate(item.captureDate)}
                </span>
              )}
              {item.fileSize !== null && item.fileSize > 0 && (
                <span className="evidence-list__card-size">
                  {formatFileSize(item.fileSize)}
                </span>
              )}
            </div>
            {item.assetId && (
              <div className="evidence-list__card-asset" title={`Asset: ${item.assetId}`}>
                Linked
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
